import BirthdayHeader from "@/components/birthday-reminder/BirthdayHeader";
import BirthdayFooter from "@/components/birthday-reminder/BirthdayFooter";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  HelpCircle,
  Rocket,
  Users,
  Bell,
  Shield,
  Settings,
  AlertCircle,
  Mail,
  Smartphone,
  Calendar,
  MessageCircle,
  Download,
} from "lucide-react";

export default function BirthdayReminderSupport() {
  const quickLinks = [
    { icon: Rocket, title: "Getting Started", href: "#getting-started" },
    { icon: Users, title: "Importing Contacts", href: "#importing-contacts" },
    { icon: Bell, title: "Notifications", href: "#notifications" },
    { icon: AlertCircle, title: "Troubleshooting", href: "#troubleshooting" },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <BirthdayHeader currentPath="/birthdayreminder/support" />

      {/* Hero Section */}
      <section className="py-16 px-4 bg-gradient-to-b from-pink-50 to-purple-50 dark:from-gray-900 dark:to-gray-800">
        <div className="container mx-auto max-w-4xl text-center space-y-6">
          <div className="w-20 h-20 bg-gradient-to-br from-pink-500 to-purple-600 rounded-3xl flex items-center justify-center mx-auto">
            <HelpCircle className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold">How Can We Help?</h1>
          <p className="text-xl text-muted-foreground">
            Find answers to common questions or get in touch with our support team
          </p>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-12 px-4">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-2xl font-bold mb-6 text-center">Popular Topics</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {quickLinks.map((link, index) => (
              <a key={index} href={link.href}>
                <Card className="hover:border-primary transition-all cursor-pointer h-full">
                  <CardContent className="p-6 text-center space-y-2">
                    <link.icon className="w-8 h-8 mx-auto text-primary" />
                    <p className="font-semibold text-sm">{link.title}</p>
                  </CardContent>
                </Card>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Sections */}
      <section className="py-12 px-4">
        <div className="container mx-auto max-w-4xl space-y-12">
          {/* Getting Started */}
          <div id="getting-started">
            <div className="flex items-center space-x-3 mb-6">
              <Rocket className="w-8 h-8 text-primary" />
              <h2 className="text-3xl font-bold">Getting Started</h2>
            </div>
            <Accordion type="single" collapsible className="space-y-4">
              <AccordionItem value="add-first" className="border rounded-lg px-6">
                <AccordionTrigger className="hover:no-underline">
                  How do I add my first birthday?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground space-y-3">
                  <p>Adding a birthday is simple:</p>
                  <ol className="list-decimal pl-6 space-y-2">
                    <li>Open the Birthday Reminder app</li>
                    <li>Tap the <strong>"+"</strong> button (usually at the bottom or top right)</li>
                    <li>Enter the person's name</li>
                    <li>Select their birth date using the date picker</li>
                    <li>Optionally add a photo, relationship note, or custom reminder time</li>
                    <li>Tap <strong>"Save"</strong></li>
                  </ol>
                  <p className="text-sm">
                    ✓ The birthday will now appear in your list and calendar view!
                  </p>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="import-contacts" className="border rounded-lg px-6">
                <AccordionTrigger className="hover:no-underline">
                  How do I import birthdays from my contacts?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground space-y-3">
                  <ol className="list-decimal pl-6 space-y-2">
                    <li>Go to the <strong>Settings</strong> tab</li>
                    <li>Tap <strong>"Import from Contacts"</strong></li>
                    <li>Grant contacts permission when prompted</li>
                    <li>Review the list of contacts with birthdays</li>
                    <li>Select the ones you want to import</li>
                    <li>Tap <strong>"Import Selected"</strong></li>
                  </ol>
                  <p className="text-sm">
                    The app will automatically detect and prevent duplicate imports.
                  </p>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="set-reminders" className="border rounded-lg px-6">
                <AccordionTrigger className="hover:no-underline">
                  How do I set up reminders?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground space-y-3">
                  <p>Reminders are enabled by default, but you can customize them:</p>
                  <ol className="list-decimal pl-6 space-y-2">
                    <li>Go to <strong>Settings</strong></li>
                    <li>Tap <strong>"Notification Settings"</strong></li>
                    <li>Choose when you want to be reminded (morning of, day before, week before)</li>
                    <li>Set your preferred notification time</li>
                  </ol>
                  <p className="text-sm font-semibold">
                    Make sure to grant notification permissions when prompted!
                  </p>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="add-photo" className="border rounded-lg px-6">
                <AccordionTrigger className="hover:no-underline">
                  How do I add photos to birthdays?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground space-y-3">
                  <ol className="list-decimal pl-6 space-y-2">
                    <li>When adding or editing a birthday, tap the photo placeholder</li>
                    <li>Choose <strong>"Take Photo"</strong> or <strong>"Choose from Library"</strong></li>
                    <li>Select or take a photo</li>
                    <li>Crop if needed</li>
                    <li>Save the birthday</li>
                  </ol>
                  <p className="text-sm">
                    Photos are stored locally on your device and never uploaded anywhere.
                  </p>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>

          {/* Importing Contacts */}
          <div id="importing-contacts">
            <div className="flex items-center space-x-3 mb-6">
              <Users className="w-8 h-8 text-primary" />
              <h2 className="text-3xl font-bold">Importing Contacts</h2>
            </div>
            <Accordion type="single" collapsible className="space-y-4">
              <AccordionItem value="cant-see-contacts" className="border rounded-lg px-6">
                <AccordionTrigger className="hover:no-underline">
                  Why can't I see some of my contacts?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground space-y-3">
                  <p>The import feature only shows contacts that have birthdays saved. If you can't see a contact:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>They may not have a birthday in their contact information</li>
                    <li>Check your device's Contacts app and add the birthday there first</li>
                    <li>Make sure you've granted contacts permission to Birthday Reminder</li>
                  </ul>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="duplicates" className="border rounded-lg px-6">
                <AccordionTrigger className="hover:no-underline">
                  Why are duplicates showing?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground space-y-3">
                  <p>
                    The app uses intelligent duplicate detection, but similar names may still appear. 
                    Duplicates are marked with a badge, and you can:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Check the duplicate indicator before importing</li>
                    <li>Manually review similar names</li>
                    <li>Skip importing contacts that already exist</li>
                  </ul>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="no-year" className="border rounded-lg px-6">
                <AccordionTrigger className="hover:no-underline">
                  What if contacts don't have birth years?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground space-y-3">
                  <p>
                    No problem! Birthday Reminder can import birthdays even without the year:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>You'll still get reminders on the correct date</li>
                    <li>Age won't be calculated (since the year is unknown)</li>
                    <li>You can manually add the year later by editing the birthday</li>
                  </ul>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="reimport" className="border rounded-lg px-6">
                <AccordionTrigger className="hover:no-underline">
                  How do I re-import after adding new contacts?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground space-y-3">
                  <p>Simply go through the import process again:</p>
                  <ol className="list-decimal pl-6 space-y-2">
                    <li>Settings → Import from Contacts</li>
                    <li>The app will show new contacts and skip ones you've already imported</li>
                    <li>Select and import the new contacts</li>
                  </ol>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>

          {/* Notifications */}
          <div id="notifications">
            <div className="flex items-center space-x-3 mb-6">
              <Bell className="w-8 h-8 text-primary" />
              <h2 className="text-3xl font-bold">Notifications</h2>
            </div>
            <Accordion type="single" collapsible className="space-y-4">
              <AccordionItem value="not-receiving" className="border rounded-lg px-6">
                <AccordionTrigger className="hover:no-underline">
                  I'm not receiving notifications - why?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground space-y-3">
                  <p>Check these common solutions:</p>
                  <div className="space-y-4">
                    <div>
                      <p className="font-semibold text-foreground mb-2">iOS:</p>
                      <ol className="list-decimal pl-6 space-y-1 text-sm">
                        <li>Go to iPhone Settings → Notifications → Birthday Reminder</li>
                        <li>Make sure "Allow Notifications" is turned ON</li>
                        <li>Check that alerts are set to "Immediate Delivery"</li>
                        <li>Ensure "Do Not Disturb" or "Focus" isn't blocking notifications</li>
                      </ol>
                    </div>
                    <div>
                      <p className="font-semibold text-foreground mb-2">Android:</p>
                      <ol className="list-decimal pl-6 space-y-1 text-sm">
                        <li>Go to Settings → Apps → Birthday Reminder → Notifications</li>
                        <li>Make sure notifications are enabled</li>
                        <li>Check that "Do Not Disturb" isn't active</li>
                        <li>Verify battery optimization isn't blocking the app</li>
                      </ol>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="change-time" className="border rounded-lg px-6">
                <AccordionTrigger className="hover:no-underline">
                  How do I change when I get reminded?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground space-y-3">
                  <p><strong>Global settings (for all birthdays):</strong></p>
                  <ol className="list-decimal pl-6 space-y-2">
                    <li>Go to Settings → Notification Settings</li>
                    <li>Choose your preferred reminder time</li>
                    <li>Select when to be reminded (day of, day before, week before)</li>
                  </ol>
                  <p className="mt-4"><strong>Per-person settings:</strong></p>
                  <ol className="list-decimal pl-6 space-y-2">
                    <li>Tap on a birthday to open details</li>
                    <li>Tap "Edit"</li>
                    <li>Scroll to "Custom Reminder Time"</li>
                    <li>Set a specific time for this person</li>
                  </ol>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="multiple-reminders" className="border rounded-lg px-6">
                <AccordionTrigger className="hover:no-underline">
                  Can I set multiple reminders per birthday?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground space-y-3">
                  <p>
                    Currently, Birthday Reminder supports one reminder per birthday. However, you can:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Set global reminders (e.g., always remind 1 week before)</li>
                    <li>Use your device's calendar app for additional reminders</li>
                  </ul>
                  <p className="text-sm">
                    Multiple reminders per birthday is on our roadmap for future updates!
                  </p>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="turn-off-specific" className="border rounded-lg px-6">
                <AccordionTrigger className="hover:no-underline">
                  How do I turn off notifications for specific people?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground space-y-3">
                  <ol className="list-decimal pl-6 space-y-2">
                    <li>Tap on the birthday you want to silence</li>
                    <li>Tap "Edit"</li>
                    <li>Turn off "Reminders" toggle</li>
                    <li>Save</li>
                  </ol>
                  <p className="text-sm">
                    The birthday will stay in your list, but you won't receive notifications for it.
                  </p>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>

          {/* Features & Data */}
          <div id="features">
            <div className="flex items-center space-x-3 mb-6">
              <Settings className="w-8 h-8 text-primary" />
              <h2 className="text-3xl font-bold">Features & Data</h2>
            </div>
            <Accordion type="single" collapsible className="space-y-4">
              <AccordionItem value="age-calc" className="border rounded-lg px-6">
                <AccordionTrigger className="hover:no-underline">
                  How does age calculation work?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground space-y-3">
                  <p>
                    Birthday Reminder automatically calculates age when you include the birth year:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Shows current age on birthday cards</li>
                    <li>Displays "turning X" for upcoming birthdays</li>
                    <li>Updates automatically after each birthday</li>
                  </ul>
                  <p className="text-sm">
                    If you don't include the year, the app will just show the date without age calculation.
                  </p>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="edit-delete" className="border rounded-lg px-6">
                <AccordionTrigger className="hover:no-underline">
                  How do I edit or delete a birthday?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground space-y-3">
                  <p><strong>To edit:</strong></p>
                  <ol className="list-decimal pl-6 space-y-1">
                    <li>Tap on the birthday card</li>
                    <li>Tap "Edit" button</li>
                    <li>Make your changes</li>
                    <li>Tap "Save"</li>
                  </ol>
                  <p className="mt-3"><strong>To delete:</strong></p>
                  <ol className="list-decimal pl-6 space-y-1">
                    <li>Swipe left on the birthday (or long-press)</li>
                    <li>Tap "Delete"</li>
                    <li>Confirm deletion</li>
                  </ol>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="calendar-view" className="border rounded-lg px-6">
                <AccordionTrigger className="hover:no-underline">
                  What's the calendar view?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground space-y-3">
                  <p>
                    The calendar view shows all birthdays in a visual monthly format:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Tap the Calendar tab to access it</li>
                    <li>Swipe to navigate between months</li>
                    <li>Birthdays appear as dots or highlights on their dates</li>
                    <li>Tap a date to see who has a birthday that day</li>
                  </ul>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="messages" className="border rounded-lg px-6">
                <AccordionTrigger className="hover:no-underline">
                  How do I use auto-drafted messages?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground space-y-3">
                  <ol className="list-decimal pl-6 space-y-2">
                    <li>Tap on a birthday card</li>
                    <li>Tap the "Send Message" button</li>
                    <li>Choose your device's messaging app (SMS, WhatsApp, etc.)</li>
                    <li>A pre-written birthday message will be ready</li>
                    <li>Edit if needed and send!</li>
                  </ol>
                  <p className="text-sm">
                    The message opens in your messaging app - Birthday Reminder doesn't send messages directly.
                  </p>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="data-stored" className="border rounded-lg px-6">
                <AccordionTrigger className="hover:no-underline">
                  Where is my data stored?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground space-y-3">
                  <p className="font-semibold text-foreground">
                    All your data is stored locally on your device.
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>No cloud storage or servers</li>
                    <li>Data never leaves your device</li>
                    <li>Complete privacy and security</li>
                    <li>May be included in device backups (iCloud, Google Drive)</li>
                  </ul>
                  <p className="text-sm">
                    See our <a href="/birthdayreminder/privacy" className="text-primary hover:underline">Privacy Policy</a> for more details.
                  </p>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="backup" className="border rounded-lg px-6">
                <AccordionTrigger className="hover:no-underline">
                  How do I backup or restore my data?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground space-y-3">
                  <p>
                    Your birthdays can be backed up through your device's native backup:
                  </p>
                  <div className="space-y-3">
                    <div>
                      <p className="font-semibold text-foreground">iOS (iCloud Backup):</p>
                      <p className="text-sm">
                        If iCloud backup is enabled, your Birthday Reminder data is automatically included. 
                        When you restore your iPhone, the app data will be restored too.
                      </p>
                    </div>
                    <div>
                      <p className="font-semibold text-foreground">Android (Google Drive Backup):</p>
                      <p className="text-sm">
                        Enable backup in Settings → Google → Backup. Your app data will be backed up 
                        and restored when you set up a new device.
                      </p>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>

          {/* Troubleshooting */}
          <div id="troubleshooting">
            <div className="flex items-center space-x-3 mb-6">
              <AlertCircle className="w-8 h-8 text-primary" />
              <h2 className="text-3xl font-bold">Troubleshooting</h2>
            </div>
            <Accordion type="single" collapsible className="space-y-4">
              <AccordionItem value="app-crashing" className="border rounded-lg px-6">
                <AccordionTrigger className="hover:no-underline">
                  The app keeps crashing - what should I do?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground space-y-3">
                  <p>Try these solutions in order:</p>
                  <ol className="list-decimal pl-6 space-y-2">
                    <li><strong>Force close and reopen</strong> the app</li>
                    <li><strong>Restart your device</strong></li>
                    <li><strong>Check for updates</strong> in the App Store or Google Play</li>
                    <li><strong>Clear app cache</strong> (Android: Settings → Apps → Birthday Reminder → Clear Cache)</li>
                    <li><strong>Reinstall the app</strong> (your data should be preserved if you have device backups enabled)</li>
                  </ol>
                  <p className="text-sm">
                    If the problem persists, please contact us with your device model and OS version.
                  </p>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="data-loss" className="border rounded-lg px-6">
                <AccordionTrigger className="hover:no-underline">
                  I lost my data after an update - can I recover it?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground space-y-3">
                  <p>If you've lost data:</p>
                  <ol className="list-decimal pl-6 space-y-2">
                    <li>Check if you have a device backup (iCloud or Google Drive)</li>
                    <li>Restore from that backup to recover your data</li>
                    <li>If no backup exists, unfortunately the data cannot be recovered</li>
                  </ol>
                  <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mt-4">
                    <p className="text-blue-800 dark:text-blue-200 text-sm">
                      <strong>Tip:</strong> Enable automatic device backups to prevent future data loss.
                    </p>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="contacts-not-importing" className="border rounded-lg px-6">
                <AccordionTrigger className="hover:no-underline">
                  Contacts aren't importing - what's wrong?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground space-y-3">
                  <p>Check these potential issues:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>
                      <strong>Permission:</strong> Go to device Settings → Birthday Reminder → ensure 
                      Contacts permission is granted
                    </li>
                    <li>
                      <strong>No birthdays in contacts:</strong> Make sure your contacts actually have 
                      birthdays saved in the Contacts app
                    </li>
                    <li>
                      <strong>Restart the app:</strong> Force close and reopen
                    </li>
                    <li>
                      <strong>Update:</strong> Make sure you have the latest version
                    </li>
                  </ul>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="wrong-device" className="border rounded-lg px-6">
                <AccordionTrigger className="hover:no-underline">
                  I got a new phone - how do I transfer my birthdays?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground space-y-3">
                  <div className="space-y-3">
                    <div>
                      <p className="font-semibold text-foreground">iOS to iOS:</p>
                      <ol className="list-decimal pl-6 space-y-1 text-sm">
                        <li>Back up old iPhone to iCloud</li>
                        <li>Set up new iPhone and restore from iCloud backup</li>
                        <li>Your Birthday Reminder data will be restored</li>
                      </ol>
                    </div>
                    <div>
                      <p className="font-semibold text-foreground">Android to Android:</p>
                      <ol className="list-decimal pl-6 space-y-1 text-sm">
                        <li>Ensure Google Drive backup is enabled on old phone</li>
                        <li>Sign in with same Google account on new phone</li>
                        <li>Install Birthday Reminder - data should restore automatically</li>
                      </ol>
                    </div>
                    <div>
                      <p className="font-semibold text-foreground">Between iOS and Android:</p>
                      <p className="text-sm">
                        Direct transfer isn't possible. You'll need to re-import from contacts or manually 
                        add birthdays again.
                      </p>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </section>

      {/* Contact Support */}
      <section className="py-20 px-4 bg-gradient-to-br from-pink-50 to-purple-50 dark:from-gray-900 dark:to-gray-800">
        <div className="container mx-auto max-w-4xl">
          <Card className="border-2">
            <CardContent className="p-8 space-y-6">
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-gradient-to-br from-pink-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto">
                  <Mail className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-3xl font-bold">Still Need Help?</h2>
                <p className="text-lg text-muted-foreground">
                  Can't find what you're looking for? Our support team is here to help.
                </p>
              </div>

              <div className="bg-muted rounded-xl p-6 space-y-4">
                <h3 className="font-semibold text-lg">When contacting support, please include:</h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start space-x-2">
                    <Smartphone className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span><strong>Device type:</strong> iPhone 14, Samsung Galaxy S23, etc.</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Settings className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span><strong>OS version:</strong> iOS 17, Android 13, etc.</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Download className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span><strong>App version:</strong> Found in Settings → About</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <MessageCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span><strong>Detailed description:</strong> What happened and what you expected</span>
                  </li>
                </ul>
              </div>

              <div className="text-center space-y-4">
                <Button size="lg" asChild>
                  <a href="mailto:developer@designr.co?subject=Birthday%20Reminder%20Support">
                    <Mail className="w-5 h-5 mr-2" />
                    Email Support
                  </a>
                </Button>
                <p className="text-sm text-muted-foreground">
                  developer@designr.co • We typically respond within 24-48 hours
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Download CTA */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-4xl text-center space-y-6">
          <h2 className="text-3xl font-bold">Don't Have the App Yet?</h2>
          <p className="text-lg text-muted-foreground">
            Download Birthday Reminder and never forget a birthday again
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" variant="outline" asChild>
              <a href="/birthdayreminder">
                View Features
              </a>
            </Button>
            <Button size="lg" asChild>
              <a href="/birthdayreminder">
                <Download className="w-5 h-5 mr-2" />
                Download Free
              </a>
            </Button>
          </div>
        </div>
      </section>

      <BirthdayFooter />
    </div>
  );
}

